/**
 * Objectifs d'un challenge — les TROIS familles, source unique (pure, client + serveur).
 *
 * Décision 4.1 (PR #7) : l'écran Challenges de Pilot ne proposait plus que les critères du
 * simulateur ; les objectifs historiques (« le plus de rendez-vous »…) et communs (« score
 * cumulé », « meilleure moyenne ») n'étaient plus sélectionnables. La liste de l'ancien écran
 * (`AccountPage`) est extraite ici et lue par le composant partagé `ChallengeObjectiveSelect`,
 * utilisé par l'ancien ET le nouvel écran.
 *
 *   1. objectifs propres au simulateur (clés de règle historiques, selon son nom) ;
 *   2. critères de score du simulateur (libellés via `pickScoringLabel`, jamais de ternaire) ;
 *   3. objectifs communs à tous les simulateurs.
 */
import { pickScoringLabel } from "./scoring-i18n";

export type ObjectiveFamily = "simulator" | "criterion" | "common";
export type ObjectiveOption = { value: string; family: ObjectiveFamily; labelKey?: string; label?: string };

/** Objectifs historiques propres à un simulateur, repérés par son nom (en minuscules). */
export const SIMULATOR_OBJECTIVES: Record<string, Array<{ value: string; labelKey: string }>> = {
  prospector: [{ value: "most_rdv", labelKey: "challenge.objectiveMostRdv" }],
  discovery: [{ value: "most_needs", labelKey: "challenge.objectiveMostNeeds" }],
  negociator: [{ value: "deal_closed", labelKey: "challenge.objectiveDealClosed" }],
  pitch: [{ value: "pitch_validated", labelKey: "challenge.objectivePitchValidated" }],
  satisfactor: [
    { value: "tension_mastered", labelKey: "challenge.objectiveTensionMastered" },
    { value: "clear_outcome", labelKey: "challenge.objectiveClearOutcome" },
  ],
};

/** Objectifs communs à tous les simulateurs. */
export const COMMON_OBJECTIVES: Array<{ value: string; labelKey: string }> = [
  { value: "cumulative_score", labelKey: "challenge.objectiveCumulativeScore" },
  { value: "best_average", labelKey: "challenge.objectiveBestAverage" },
];

/** Libellé de chaque clé de règle (familles 1 et 3), pour l'affichage d'un challenge existant. */
export const RULE_OBJECTIVE_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  [...Object.values(SIMULATOR_OBJECTIVES).flat(), ...COMMON_OBJECTIVES].map((o) => [o.value, o.labelKey]),
);

/** Critères « note globale » : ils ne font pas un objectif. */
const OVERALL_KEYS = ["overall", "global", "score_total", "total_score"];

type CriterionLike = { key: string; type?: string | null; label?: string | null; labelFr?: string | null; labelEn?: string | null; labelSpoken?: string | null };

/** Les critères du simulateur qui peuvent servir d'objectif (notes, hors note globale). */
function scoreCriteria(criteria: CriterionLike[]): CriterionLike[] {
  return criteria.filter((c) => (c.type ?? "score") === "score" && !OVERALL_KEYS.includes(c.key));
}

/** Toutes les options, dans l'ordre des familles, sans doublon. */
export function challengeObjectiveOptions(simulatorName: string | null | undefined, criteria: CriterionLike[], lang: string): ObjectiveOption[] {
  const out: ObjectiveOption[] = [];
  const seen = new Set<string>();
  const push = (o: ObjectiveOption) => { if (!seen.has(o.value)) { seen.add(o.value); out.push(o); } };
  for (const o of SIMULATOR_OBJECTIVES[(simulatorName ?? "").trim().toLowerCase()] ?? []) push({ value: o.value, family: "simulator", labelKey: o.labelKey });
  for (const c of scoreCriteria(criteria)) push({ value: c.key, family: "criterion", label: pickScoringLabel(c, lang) || c.key });
  for (const o of COMMON_OBJECTIVES) push({ value: o.value, family: "common", labelKey: o.labelKey });
  return out;
}

/** Libellé d'un objectif enregistré (quelle que soit sa famille) ; la clé brute en dernier recours. */
export function challengeObjectiveLabel(key: string | null | undefined, criteria: CriterionLike[], lang: string, t: (k: string) => string): string {
  if (!key) return "";
  const c = criteria.find((x) => x.key === key);
  if (c) return pickScoringLabel(c, lang) || key;
  const ruleKey = RULE_OBJECTIVE_LABEL_KEYS[key];
  return ruleKey ? t(ruleKey) : key;
}
